/**
 * JobTypeCard Component
 * Premium card for selecting job analysis type with Lucide icons
 */

import React from "react";
import { cn } from "../../utils";
import { Card } from "../common";
import { JobType } from "../../types";
import { getJobTypeConfig } from "../../services/jobTypes";
import {
  Users,
  Car,
  ParkingCircle,
  ArrowLeftRight,
  CircleDot,
  Utensils,
  BedDouble,
  Building2,
  PawPrint,
  HardHat,
  Box,
  MonitorPlay,
  BarChart3,
} from "lucide-react";

// Job type to icon mapping
const JOB_TYPE_ICONS: Record<string, React.ReactNode> = {
  people_counting: <Users className="w-7 h-7" />,
  number_plate_detection: <Car className="w-7 h-7" />,
  parking_analysis: <ParkingCircle className="w-7 h-7" />,
  in_out_counting: <ArrowLeftRight className="w-7 h-7" />,
  pothole_detection: <CircleDot className="w-7 h-7" />,
  food_waste_estimation: <Utensils className="w-7 h-7" />,
  room_readiness: <BedDouble className="w-7 h-7" />,
  lobby_crowd_detection: <Building2 className="w-7 h-7" />,
  wildlife_detection: <PawPrint className="w-7 h-7" />,
  ppe_detection: <HardHat className="w-7 h-7" />,
  damage_detection_3d: <Box className="w-7 h-7" />,
  multi_camera: <MonitorPlay className="w-7 h-7" />,
};

interface JobTypeCardProps {
  jobType: JobType;
  onClick: (jobType: JobType) => void;
  className?: string;
}

export const JobTypeCard: React.FC<JobTypeCardProps> = ({
  jobType,
  onClick,
  className,
}) => {
  const config = getJobTypeConfig(jobType);
  const icon = JOB_TYPE_ICONS[jobType] || <BarChart3 className="w-7 h-7" />;

  return (
    <Card
      variant="glass"
      padding="md"
      hover
      onClick={() => onClick(jobType)}
      className={cn("cursor-pointer group relative overflow-hidden", className)}
    >
      <div className="flex flex-col items-center text-center gap-3">
        {/* Icon */}
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center text-primary-400 group-hover:scale-110 transition-transform duration-200">
          {icon}
        </div>

        {/* Title */}
        <h3 className="text-base font-semibold text-white group-hover:text-primary-400 transition-colors">
          {config.label}
        </h3>

        {/* Description */}
        <p className="text-sm text-slate-400 line-clamp-2">
          {config.description}
        </p>

        {/* Category badge */}
        <span
          className={cn(
            "inline-block px-2.5 py-1 text-xs font-medium rounded-lg border",
            config.category === "advanced"
              ? "bg-accent-500/20 text-accent-400 border-accent-500/30"
              : "bg-primary-500/20 text-primary-400 border-primary-500/30"
          )}
        >
          {config.category === "advanced" ? "Advanced" : "Standard"}
        </span>
      </div>

      {/* Hover gradient effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" />
    </Card>
  );
};
